import { useSelector } from "react-redux";
import emailjs from "emailjs-com";
import React, { useState, useEffect, useCallback } from "react";
import classes from "./SendMail.module.css";
import { appendError } from "../../utils/errorLog";

import Loader from "react-loader-spinner";

import CountdownRedirect from "./CountdownRedirect";
import { generateDatesBetween, numberWithCommas } from "../UI/Helper";

const {
  REACT_APP_EMAILJS_SERVICE_ID,
  REACT_APP_EMAILJS_TEMPLATE_ID,
  REACT_APP_EMAILJS_USER_ID,
} = process.env;

const apiKey = process.env.REACT_APP_API_RETURNING_KEY;

const SendEmail = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [sendStatus, setSendStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  //form the form state
  const firstName = useSelector((state) => state.form.firstName);
  const lastName = useSelector((state) => state.form.lastName);
  const email = useSelector((state) => state.form.email);
  const phone = useSelector((state) => state.form.phone);
  const countryOriginal = useSelector((state) => state.form.country);
  let country = countryOriginal;
  country = phone ? `${country} (${phone})` : country;
  const additionalRequest = useSelector(
    (state) => state.form.additionalRequest
  );

  // //from the quote state
  const airportPickUp = useSelector((state) => state.quote.airportPickUp);
  const airportDropOff = useSelector((state) => state.quote.airportDropOff);
  const extraBed = useSelector((state) => state.quote.extraBed);
  const accomPref = useSelector((state) => state.quote.accomPref);
  const promotionName = useSelector((state) => state.quote.promotionName);
  const wellnessPackageCost = useSelector(
    (state) => state.quote.wellnessPackageCost
  );
  const totalDiscount = useSelector((state) => state.quote.totalDiscount);
  const transferCost = useSelector((state) => state.quote.transferCost);
  const grandTotal = useSelector((state) => state.quote.grandTotal);

  // //from the booking state
  const checkInDate = useSelector((state) => state.booking.checkInDate);
  const checkOutDate = useSelector((state) => state.booking.checkOutDate);
  const accommNights = useSelector((state) => state.booking.accommNights);
  const programDays = useSelector((state) => state.booking.programDays);
  const totalRooms = useSelector((state) => state.booking.totalRooms);
  const totalGuests = useSelector((state) => state.booking.totalGuests);
  const guest1Returning = useSelector((state) => state.booking.guest1Returning);
  const guest1ReturningEmail = useSelector(
    (state) => state.booking.guest1ReturningEmail
  );
  const guest2Returning = useSelector((state) => state.booking.guest2Returning);
  const guest2ReturningEmail = useSelector(
    (state) => state.booking.guest2ReturningEmail
  );

  const getName = useCallback(() => {
    return `Guest Name: ${firstName} ${lastName}`;
  }, [firstName, lastName]);

  const getEmail = useCallback(() => {
    return `${email}`;
  }, [email]);

  const getDepartureCountry = useCallback(() => {
    return `Departure Country: ${country}`;
  }, [country]);

  const getTravelDates = useCallback(() => {
    return `Travel Dates: ${checkInDate} (Check In) - ${checkOutDate} (Check Out) `;
  }, [checkInDate, checkOutDate]);

  const getAccommodation = useCallback(() => {
    return `Accommodation (${totalRooms} Room): ${accommNights} Nights Accommodation ${
      extraBed ? "(EXTRA BED REQUESTED)" : ""
    }`;
  }, [totalRooms, accommNights, extraBed]);

  const getProgram = useCallback(() => {
    return `Program (${totalGuests} Guest): ${programDays} Days Wellness Program`;
  }, [totalGuests, programDays]);

  const getReturning = useCallback(() => {
    return `Returning Guest: Guest 1 (${
      guest1Returning ? "Returning " + guest1ReturningEmail : "First Time"
    }) ${
      totalGuests > 1
        ? `Guest 2 (${
            guest2Returning ? "Returning " + guest2ReturningEmail : "First Time"
          })`
        : ""
    }`;
  }, [
    guest1Returning,
    totalGuests,
    guest2Returning,
    guest1ReturningEmail,
    guest2ReturningEmail,
  ]);

  const getAirportPickup = useCallback(() => {
    return `Airport Pickup: ${airportPickUp ? "Requested" : "Not Requested"}`;
  }, [airportPickUp]);

  const getDropOff = useCallback(() => {
    return `Airport Dropoff: ${airportDropOff ? "Requested" : "Not Requested"}`;
  }, [airportDropOff]);

  const getAccomPref = useCallback(() => {
    let preference = "No Preference";
    if (accomPref === "ground") {
      preference = "Ground Floor";
    }
    if (accomPref === "first") {
      preference = "First Floor";
    }
    if (accomPref === "bamboo") {
      preference = "Bamboo Room";
    }

    return `Accommodation Preference: ${preference}`;
  }, [accomPref]);

  // Get rates for discount calculation
  const specialRates = useSelector((state) => state.rates.specialRates);
  const defaultRate = useSelector((state) => state.rates.defaultRate);
  const baseAccommodationRate = useSelector((state) => state.rates.baseAccommodationRate);
  const baseProgramRate = useSelector((state) => state.rates.baseProgramRate);

  const getDiscountsForDate = useCallback((dateString) => {
    const specialRate = specialRates.find(rate => {
      return dateString === rate.date;
    });
    if (specialRate) {
      return { 
        newGuestDiscount: specialRate.newGuestDiscount,
        returningGuestDiscount: specialRate.returningGuestDiscount,
        promotionName: specialRate.name
      };
    } else {
      return { 
        newGuestDiscount: defaultRate.newGuestDiscount,
        returningGuestDiscount: defaultRate.returningGuestDiscount,
        promotionName: 'default'
      };
    }
  }, [specialRates, defaultRate]);

  const getQuote = useCallback(() => {
    const wellnessPackage = `${numberWithCommas(wellnessPackageCost)} THB Wellness Package (Accommodation + Wellness Program)`;
    const airportTransferCost = transferCost > 0 ? `${numberWithCommas(transferCost)} THB Airport Transfers` : "";
    const quoteTotal = `${numberWithCommas(grandTotal)} THB Total Quotation`;

    // Calculate discount breakdown
    const multipleRooms = totalRooms === 2;
    const multipleGuests = totalGuests === 2;
    
    let discountBreakdown = "";
    
    if (multipleGuests) {
      const datesBetween = generateDatesBetween(checkInDate, checkOutDate);
      const accommodationDates = datesBetween.slice(0, -1);
      const programDates = datesBetween.slice(1, -1);
      
      // Calculate accommodation discounts
      let accommodationDiscountGuest1 = 0;
      let accommodationDiscountGuest2 = 0;
      let roomDiscountPercent = null;
      let room1DiscountPercent = null;
      let room2DiscountPercent = null;
      
      if (accommodationDates.length > 0) {
        const firstDateDiscounts = getDiscountsForDate(accommodationDates[0]);
        
        if (multipleRooms) {
          // 2 rooms
          room1DiscountPercent = guest1Returning 
            ? Math.round(firstDateDiscounts.returningGuestDiscount * 100)
            : Math.round(firstDateDiscounts.newGuestDiscount * 100);
          room2DiscountPercent = guest2Returning 
            ? Math.round(firstDateDiscounts.returningGuestDiscount * 100)
            : Math.round(firstDateDiscounts.newGuestDiscount * 100);
          
          for (const date of accommodationDates) {
            const { newGuestDiscount, returningGuestDiscount } = getDiscountsForDate(date);
            const discountToUseRoom1 = guest1Returning ? returningGuestDiscount : newGuestDiscount;
            accommodationDiscountGuest1 += baseAccommodationRate * discountToUseRoom1;
            const discountToUseRoom2 = guest2Returning ? returningGuestDiscount : newGuestDiscount;
            accommodationDiscountGuest2 += baseAccommodationRate * discountToUseRoom2;
          }
        } else {
          // 1 room
          const roomIsReturning = guest1Returning || guest2Returning;
          roomDiscountPercent = roomIsReturning
            ? Math.round(firstDateDiscounts.returningGuestDiscount * 100)
            : Math.round(firstDateDiscounts.newGuestDiscount * 100);
          
          for (const date of accommodationDates) {
            const { newGuestDiscount, returningGuestDiscount } = getDiscountsForDate(date);
            const discountToUse = roomIsReturning ? returningGuestDiscount : newGuestDiscount;
            accommodationDiscountGuest1 += baseAccommodationRate * discountToUse;
          }
        }
      }
      
      // Calculate program discounts
      let programDiscountGuest1 = 0;
      let programDiscountGuest2 = 0;
      let guest1DiscountPercent = null;
      let guest2DiscountPercent = null;
      
      if (programDates.length > 0) {
        const firstDateDiscounts = getDiscountsForDate(programDates[0]);
        
        guest1DiscountPercent = guest1Returning 
          ? Math.round(firstDateDiscounts.returningGuestDiscount * 100)
          : Math.round(firstDateDiscounts.newGuestDiscount * 100);
        guest2DiscountPercent = guest2Returning 
          ? Math.round(firstDateDiscounts.returningGuestDiscount * 100)
          : Math.round(firstDateDiscounts.newGuestDiscount * 100);
        
        for (const date of programDates) {
          const { newGuestDiscount, returningGuestDiscount } = getDiscountsForDate(date);
          const discountToUseGuest1 = guest1Returning ? returningGuestDiscount : newGuestDiscount;
          programDiscountGuest1 += baseProgramRate * discountToUseGuest1;
          const discountToUseGuest2 = guest2Returning ? returningGuestDiscount : newGuestDiscount;
          programDiscountGuest2 += baseProgramRate * discountToUseGuest2;
        }
      }
      
      // Build discount breakdown string
      if (multipleRooms) {
        discountBreakdown = `Room 1 - ${room1DiscountPercent}% ${guest1Returning ? 'Returning ' : ''}Discount: -${numberWithCommas(Math.round(accommodationDiscountGuest1))} THB\n`;
        discountBreakdown += `Guest 1 Program - ${guest1DiscountPercent}% ${guest1Returning ? 'Returning ' : ''}Discount: -${numberWithCommas(Math.round(programDiscountGuest1))} THB\n`;
        discountBreakdown += `Room 2 - ${room2DiscountPercent}% ${guest2Returning ? 'Returning ' : ''}Discount: -${numberWithCommas(Math.round(accommodationDiscountGuest2))} THB\n`;
        discountBreakdown += `Guest 2 Program - ${guest2DiscountPercent}% ${guest2Returning ? 'Returning ' : ''}Discount: -${numberWithCommas(Math.round(programDiscountGuest2))} THB`;
      } else {
        if (roomDiscountPercent !== null) {
          discountBreakdown = `Room - ${roomDiscountPercent}% ${(guest1Returning || guest2Returning) ? 'Returning ' : ''}Discount: -${numberWithCommas(Math.round(accommodationDiscountGuest1))} THB\n`;
        }
        discountBreakdown += `Guest 1 Program - ${guest1DiscountPercent}% ${guest1Returning ? 'Returning ' : ''}Discount: -${numberWithCommas(Math.round(programDiscountGuest1))} THB\n`;
        discountBreakdown += `Guest 2 Program - ${guest2DiscountPercent}% ${guest2Returning ? 'Returning ' : ''}Discount: -${numberWithCommas(Math.round(programDiscountGuest2))} THB`;
      }
    } else {
      // Single guest - use simple format
      discountBreakdown = `-${numberWithCommas(totalDiscount)} THB ${promotionName}`;
    }

    return {
      wellnessPackage,
      discountBreakdown,
      airportTransferCost,
      quoteTotal,
    };
  }, [
    wellnessPackageCost,
    totalDiscount,
    transferCost,
    promotionName,
    grandTotal,
    totalRooms,
    totalGuests,
    guest1Returning,
    guest2Returning,
    checkInDate,
    checkOutDate,
    getDiscountsForDate,
    baseAccommodationRate,
    baseProgramRate,
  ]);

  const GetAdditionalRequest = useCallback(() => {
    return `Additional Request: ${additionalRequest}`;
  }, [additionalRequest]);

  useEffect(() => {
    const guestName = getName();
    const guestEmail = getEmail();
    const departureCountry = getDepartureCountry();
    const travelDates = getTravelDates();
    const accommodation = getAccommodation();
    const program = getProgram();
    const returning = getReturning();
    const pickup = getAirportPickup();
    const dropoff = getDropOff();
    const additionalRequest = GetAdditionalRequest();
    const accommodationPref = getAccomPref();
    const {
      wellnessPackage,
      discountBreakdown,
      airportTransferCost,
      quoteTotal,
    } = getQuote();

    const templateParams = {
      guestname: guestName,
      email: guestEmail,
      departureCountry: departureCountry,
      travelDates: travelDates,
      accommodation: accommodation,
      program: program,
      returning: returning,
      pickup: pickup,
      dropoff: dropoff,
      wellnessPackage: wellnessPackage,
      discountBreakdown: discountBreakdown,
      airportTransferCost: airportTransferCost,
      quoteTotal: quoteTotal,
      additionalRequest: additionalRequest,
      accommodationPref: accommodationPref,
    };

    //console.log(templateParams)

    // Function to log enquiry submission - called BEFORE email send to ensure it logs even if email fails
    const logEnquirySubmission = () => {
      const payload = {
        firstName: firstName,
        lastName: lastName,
        email: email,
        phone: phone || '',
        country: countryOriginal || '',
        arrivalDate: checkInDate,
        departureDate: checkOutDate,
        totalRooms: totalRooms,
        totalGuests: totalGuests,
        guest1Returning: guest1Returning,
        guest1ReturningEmail: guest1ReturningEmail || '',
        guest2Returning: guest2Returning,
        guest2ReturningEmail: guest2ReturningEmail || '',
        grandTotal: numberWithCommas(grandTotal),
        timezone: (() => {
          try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            return tz && typeof tz === "string" ? tz : "Unknown";
          } catch {
            return "Unknown";
          }
        })(),
        apiKey: apiKey,
      };

      try {
        const json = JSON.stringify(payload);
        if (navigator.sendBeacon) {
          const blob = new Blob([json], { type: "application/json" });
          navigator.sendBeacon("https://api.amitywellness.com/api-log-enquiry.php", blob);
        } else {
          fetch("https://api.amitywellness.com/api-log-enquiry.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            keepalive: true
          }).catch(() => {});
        }
      } catch {
        // silent fail
      }
    };

    async function sendMail() {
      // Log enquiry submission BEFORE sending email
      // This ensures we have a record even if EmailJS fails or shows technical error
      logEnquirySubmission();

      emailjs
        .send(
          REACT_APP_EMAILJS_SERVICE_ID,
          REACT_APP_EMAILJS_TEMPLATE_ID,
          templateParams,
          REACT_APP_EMAILJS_USER_ID
        )
        .then(
          function (response) {
            setSendStatus(true);
            //console.log(response.status, response.text)
            setIsLoading(false);
          },
          function (err) {
            appendError("EmailJS", err, {
              guestEmail: getEmail(),
              checkIn: checkInDate,
              checkOut: checkOutDate,
            });
            setSendStatus(false);
            setIsLoading(false);
            console.log(err);
          }
        );
    }

    sendMail();
  }, [
    firstName,
    lastName,
    email,
    phone,
    countryOriginal,
    checkInDate,
    checkOutDate,
    totalRooms,
    totalGuests,
    guest1Returning,
    guest1ReturningEmail,
    guest2Returning,
    guest2ReturningEmail,
    grandTotal,
    getName,
    getEmail,
    getDepartureCountry,
    getTravelDates,
    getAccommodation,
    getProgram,
    getReturning,
    getAirportPickup,
    getDropOff,
    getQuote,
    GetAdditionalRequest,
    getAccomPref,
  ]);

  // console.log(isLoading)

  const sendSuccessful = (
    <div className={classes.formContainer}>
      <div className={classes.outputContainer}>
        <div className={classes.submitSuccess}></div>
        <p className={classes.outputText}>
          Thank you, we have received your enquiry.
        </p>
        <p className={classes.outputText}>We will respond within 24 hours!</p>

        <CountdownRedirect />
      </div>
    </div>
  );

  const sendFailed = (
    <div className={classes.formContainer}>
      <div className={classes.outputContainer}>
        <div className={classes.submitFailed}></div>
        <p className={classes.outputText}>
          Sorry, there was a technical error!
        </p>
        <p className={classes.outputText}>
          Please contact us at info@amitywellness.com
        </p>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      {isLoading && (
        <div className={classes.formContainer}>
          <div className={classes.spinnerContainer}>
            <Loader type="Rings" color="#00BFFF" height={80} width={80} />
          </div>
          <p className={classes.loadingText}>Sending Email ....</p>
        </div>
      )}

      {!isLoading && sendStatus && sendSuccessful}

      {!isLoading && !sendStatus && sendFailed}
    </React.Fragment>
  );
};

export default SendEmail;
